/**
 * BFF Query Layer - All server queries wrapped with SolidStart query()
 * 
 * This layer provides:
 * - Proper SSR streaming with createAsync()
 * - Cache key management for invalidation
 * - Type-safe server functions
 * 
 * Routes should import from "~/server/bff" instead of "~/server/queries"
 */

import { query } from "@solidjs/router";
import { CACHE_KEYS } from "./cache-keys";
import type {
  Video,
  Channel,
  Topic,
  Prediction,
  Dossier,
  AnalysisRun,
  AnalysisTemplate,
  Conversation,
  Report,
  Notification,
  Briefing,
  TrendingTopic,
  TranscriptChunk,
  Visual,
  InboxStats,
} from "~/lib/types";
import { getEnvWithMock } from "../env";
import { requireUserId } from "../auth";
import {
  mapVideoRow,
  mapChannelRow,
  mapTopicRow,
  mapPredictionRow,
  mapDossierRow,
  mapNotificationRow,
  mapConversationRow,
  mapReportRow,
  mapAnalysisRunRow,
  mapTranscriptChunkRow,
} from "../db";
import { semanticSearch as ragSemanticSearch, type RagSnippet } from "../rag";

// ============================================================================
// Database Helper
// ============================================================================

function requireDb() {
  const env = getEnvWithMock();
  if (!env?.DB) {
    throw new Error("[BFF] D1 database is not configured");
  }
  return env.DB;
}

function mapJoinedChannel(row: any): Channel {
  return mapChannelRow({
    id: row.channel_id,
    youtube_channel_id: "",
    name: row.channel_name ?? "Unknown Channel",
    handle: row.channel_handle ?? "",
    thumbnail_url: row.channel_thumbnail ?? null,
    description: null,
    tags: "[]",
    is_monitored: 1,
    monitor_frequency: "normal",
    video_count: 0,
    prediction_count: 0,
    accuracy: null,
    last_video_date: null,
    created_at: "",
    updated_at: "",
  });
}

// ============================================================================
// INBOX QUERIES
// ============================================================================

export interface InboxFilters {
  priority?: string;
  channelId?: string;
  topicId?: string;
}

export const getInboxVideos = query(async (filters?: InboxFilters): Promise<Video[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  let sql = `
    SELECT v.*, c.name as channel_name, c.handle as channel_handle,
           c.thumbnail_url as channel_thumbnail
    FROM videos v
    JOIN channels c ON c.id = v.channel_id
    WHERE v.triage_status = 'pending'
  `;
  const binds: unknown[] = [];
  
  if (filters?.priority) {
    sql += ` AND v.priority = ?`;
    binds.push(filters.priority);
  }
  if (filters?.channelId) {
    sql += ` AND v.channel_id = ?`;
    binds.push(filters.channelId);
  }
  
  sql += ` ORDER BY 
    CASE v.priority 
      WHEN 'flash' THEN 1 
      WHEN 'high' THEN 2 
      WHEN 'normal' THEN 3 
      ELSE 4 
    END,
    v.published_at DESC
    LIMIT 100`;
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  return (results as any[]).map((row) => mapVideoRow(row, mapJoinedChannel(row)));
}, CACHE_KEYS.INBOX_VIDEOS);

export const getInboxStats = query(async (): Promise<InboxStats> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT 
      COUNT(*) as total,
      SUM(CASE WHEN priority = 'flash' THEN 1 ELSE 0 END) as flash,
      SUM(CASE WHEN priority = 'high' THEN 1 ELSE 0 END) as high,
      SUM(CASE WHEN priority = 'normal' THEN 1 ELSE 0 END) as normal,
      SUM(CASE WHEN priority = 'low' THEN 1 ELSE 0 END) as low,
      AVG(julianday('now') - julianday(created_at)) * 24 as avg_age
    FROM videos
    WHERE triage_status = 'pending'
  `).all();
  
  const row = results[0] as any;
  return {
    total: row?.total ?? 0,
    flash: row?.flash ?? 0,
    high: row?.high ?? 0,
    normal: row?.normal ?? 0,
    low: row?.low ?? 0,
    avgAge: row?.avg_age ?? 0,
  };
}, CACHE_KEYS.INBOX_STATS);

// ============================================================================
// VIDEO QUERIES
// ============================================================================

export interface VideoFilters {
  channelId?: string;
  topicTag?: string;
}

export const getVideos = query(async (filters?: VideoFilters): Promise<Video[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  let sql = `
    SELECT v.*, c.name as channel_name, c.handle as channel_handle,
           c.thumbnail_url as channel_thumbnail
    FROM videos v
    JOIN channels c ON c.id = v.channel_id
    WHERE v.triage_status = 'approved'
  `;
  const binds: unknown[] = [];
  
  if (filters?.channelId) {
    sql += ` AND v.channel_id = ?`;
    binds.push(filters.channelId);
  }
  if (filters?.topicTag) {
    sql += ` AND EXISTS (SELECT 1 FROM json_each(v.topic_tags) WHERE value = ?)`;
    binds.push(filters.topicTag);
  }
  
  sql += ` ORDER BY v.published_at DESC LIMIT 100`;
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  return (results as any[]).map((row) => mapVideoRow(row, mapJoinedChannel(row)));
}, CACHE_KEYS.VIDEOS);

export const getVideo = query(async (id: string): Promise<Video | null> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT v.*, c.name as channel_name, c.handle as channel_handle,
           c.thumbnail_url as channel_thumbnail
    FROM videos v
    JOIN channels c ON c.id = v.channel_id
    WHERE v.id = ?
  `).bind(id).all();
  
  if (results.length === 0) return null;
  
  const row = results[0] as any;
  return mapVideoRow(row, mapChannelRow({
    id: row.channel_id,
    youtube_channel_id: "",
    name: row.channel_name,
    handle: row.channel_handle,
    thumbnail_url: row.channel_thumbnail,
    description: null,
    tags: "[]",
    is_monitored: 1,
    monitor_frequency: "normal",
    video_count: 0,
    prediction_count: 0,
    accuracy: null,
    last_video_date: null,
    created_at: "",
    updated_at: "",
  }));
}, "video"); // Dynamic key handled by caller

export const getTranscript = query(async (videoId: string): Promise<TranscriptChunk[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT id, video_id, text, start_time, end_time, speaker
    FROM transcript_chunks
    WHERE video_id = ?
    ORDER BY start_time
  `).bind(videoId).all();
  
  return (results as any[]).map(row => mapTranscriptChunkRow(row));
}, "video-transcript"); // Dynamic key

export const getVisuals = query(async (videoId: string): Promise<Visual[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT id, video_id, image_url, timestamp, description
    FROM visuals
    WHERE video_id = ?
    ORDER BY timestamp
  `).bind(videoId).all();
  
  return (results as any[]).map(row => ({
    id: row.id as string,
    videoId: row.video_id as string,
    imageUrl: row.image_url as string,
    timestamp: row.timestamp as number,
    description: row.description as string,
  }));
}, "video-visuals"); // Dynamic key

// ============================================================================
// CHANNEL QUERIES
// ============================================================================

export interface ChannelFilters {
  isMonitored?: boolean;
}

export const getChannels = query(async (filters?: ChannelFilters): Promise<Channel[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  let sql = `SELECT * FROM channels`;
  const binds: unknown[] = [];
  
  if (filters?.isMonitored !== undefined) {
    sql += ` WHERE is_monitored = ?`;
    binds.push(filters.isMonitored ? 1 : 0);
  }
  
  sql += ` ORDER BY name`;
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  return (results as any[]).map(row => mapChannelRow(row));
}, CACHE_KEYS.CHANNELS);

export const getChannel = query(async (id: string): Promise<Channel | null> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT * FROM channels WHERE id = ?
  `).bind(id).all();
  
  if (results.length === 0) return null;
  return mapChannelRow(results[0] as any);
}, "channel"); // Dynamic key

// ============================================================================
// TOPIC QUERIES
// ============================================================================

export const getTopics = query(async (): Promise<Topic[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT * FROM topics ORDER BY recent_activity DESC, name
  `).all();
  
  return (results as any[]).map(row => mapTopicRow(row));
}, CACHE_KEYS.TOPICS);

export const getTopic = query(async (id: string): Promise<Topic | null> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT * FROM topics WHERE id = ?
  `).bind(id).all();
  
  if (results.length === 0) return null;
  return mapTopicRow(results[0] as any);
}, "topic");

export const getTrendingTopics = query(async (): Promise<TrendingTopic[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT id, name, video_count, channel_count, recent_activity
    FROM topics
    ORDER BY recent_activity DESC
    LIMIT 10
  `).all();
  
  return (results as any[]).map(row => ({
    id: row.id as string,
    name: row.name as string,
    videoCount: row.video_count as number,
    channelCount: row.channel_count as number,
    trend: "stable" as const,
    percentChange: 0,
  }));
}, CACHE_KEYS.TRENDING_TOPICS);

// ============================================================================
// PREDICTION QUERIES
// ============================================================================

export interface PredictionFilters {
  status?: string;
  channelId?: string;
  topicTag?: string;
}

export const getPredictions = query(async (filters?: PredictionFilters): Promise<Prediction[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  let sql = `
    SELECT p.*, v.title as video_title, c.name as channel_name
    FROM predictions p
    JOIN videos v ON v.id = p.video_id
    JOIN channels c ON c.id = p.channel_id
    WHERE 1=1
  `;
  const binds: unknown[] = [];
  
  if (filters?.status) {
    sql += ` AND p.status = ?`;
    binds.push(filters.status);
  }
  if (filters?.channelId) {
    sql += ` AND p.channel_id = ?`;
    binds.push(filters.channelId);
  }
  if (filters?.topicTag) {
    sql += ` AND EXISTS (SELECT 1 FROM json_each(p.topic_tags) WHERE value = ?)`;
    binds.push(filters.topicTag);
  }
  
  sql += ` ORDER BY p.target_date ASC NULLS LAST, p.created_at DESC LIMIT 100`;
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  return (results as any[]).map((row) => mapPredictionRow(row, undefined, mapJoinedChannel(row)));
}, CACHE_KEYS.PREDICTIONS);

export const getPrediction = query(async (id: string): Promise<Prediction | null> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT p.*, v.title as video_title, c.name as channel_name
    FROM predictions p
    JOIN videos v ON v.id = p.video_id
    JOIN channels c ON c.id = p.channel_id
    WHERE p.id = ?
  `).bind(id).all();
  
  if (results.length === 0) return null;
  return mapPredictionRow(results[0] as any, undefined, mapJoinedChannel(results[0]));
}, "prediction");

// ============================================================================
// DOSSIER QUERIES
// ============================================================================

export const getDossiers = query(async (): Promise<Dossier[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const { results } = await DB.prepare(`
    SELECT * FROM dossiers WHERE user_id = ? ORDER BY updated_at DESC
  `).bind(userId).all();
  
  return (results as any[]).map(row => mapDossierRow(row));
}, CACHE_KEYS.DOSSIERS);

export const getDossier = query(async (id: string): Promise<Dossier | null> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const { results: dossierResults } = await DB.prepare(`
    SELECT * FROM dossiers WHERE id = ? AND user_id = ?
  `).bind(id, userId).all();
  
  if (dossierResults.length === 0) return null;
  
  const dossier = mapDossierRow(dossierResults[0] as any);
  
  // Load sections and items
  const { results: sectionResults } = await DB.prepare(`
    SELECT * FROM dossier_sections WHERE dossier_id = ? AND user_id = ? ORDER BY sort_order
  `).bind(id, userId).all();
  
  for (const section of sectionResults as any[]) {
      const { results: itemResults } = await DB.prepare(`
        SELECT * FROM dossier_items WHERE section_id = ? AND user_id = ? ORDER BY sort_order
      `).bind(section.id, userId).all();
    
    dossier.sections.push({
      id: section.id,
      dossierId: section.dossier_id,
      name: section.name,
      order: section.sort_order,
      items: (itemResults as any[]).map(item => ({
        id: item.id,
        sectionId: item.section_id,
        type: item.item_type,
        content: item.content,
        metadata: item.metadata ? JSON.parse(item.metadata) : undefined,
        order: item.sort_order,
        createdAt: item.created_at,
      })),
    });
  }
  
  return dossier;
}, "dossier"); // Dynamic key

// ============================================================================
// ANALYSIS QUERIES
// ============================================================================

export const getAnalysisRuns = query(async (): Promise<AnalysisRun[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT * FROM analysis_runs ORDER BY created_at DESC LIMIT 50
  `).all();
  
  return (results as any[]).map(row => mapAnalysisRunRow(row));
}, CACHE_KEYS.ANALYSIS_RUNS);

export interface AnalysisHistoryFilters {
  templateId?: string;
  limit?: number;
}

export const getAnalysisHistory = query(async (filters?: AnalysisHistoryFilters): Promise<AnalysisRun[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const limit = filters?.limit ?? 50;
  
  let sql = `SELECT * FROM analysis_runs`;
  const binds: unknown[] = [];
  
  if (filters?.templateId) {
    sql += ` WHERE template_id = ?`;
    binds.push(filters.templateId);
  }
  
  sql += ` ORDER BY created_at DESC LIMIT ?`;
  binds.push(limit);
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  return (results as any[]).map(row => mapAnalysisRunRow(row));
}, CACHE_KEYS.ANALYSIS_RUNS);

export const getAnalysisRun = query(async (id: string): Promise<AnalysisRun | null> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT * FROM analysis_runs WHERE id = ?
  `).bind(id).all();
  
  if (results.length === 0) return null;
  
  const run = mapAnalysisRunRow(results[0] as any);
  
  // Load results
  const { results: resultRows } = await DB.prepare(`
    SELECT ar.*, v.title as video_title
    FROM analysis_results ar
    JOIN videos v ON v.id = ar.video_id
    WHERE ar.run_id = ?
  `).bind(id).all();
  
  run.results = (resultRows as any[]).map(row => ({
    id: row.id,
    runId: row.run_id,
    videoId: row.video_id,
    fields: JSON.parse(row.fields || "{}"),
    evidence: JSON.parse(row.evidence || "{}"),
    reviewStatus: row.review_status,
  }));
  
  return run;
}, "analysis-run"); // Dynamic key

export const getAnalysisTemplates = query(async (): Promise<AnalysisTemplate[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT * FROM analysis_templates ORDER BY is_built_in DESC, name
  `).all();
  
  return (results as any[]).map(row => ({
    id: row.id as string,
    name: row.name as string,
    description: row.description as string | undefined,
    mode: row.mode as "extraction" | "synthesis",
    schema: JSON.parse((row.schema as string) || "{}"),
    isBuiltIn: (row.is_built_in as number) === 1,
    createdAt: row.created_at as string,
  }));
}, CACHE_KEYS.ANALYSIS_TEMPLATES);

// ============================================================================
// CONVERSATION QUERIES
// ============================================================================

export const getConversations = query(async (): Promise<Conversation[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const { results } = await DB.prepare(`
    SELECT * FROM conversations 
    WHERE user_id = ?
    ORDER BY updated_at DESC 
    LIMIT 50
  `).bind(userId).all();
  
  return (results as any[]).map(row => mapConversationRow(row));
}, CACHE_KEYS.CONVERSATIONS);

export const getConversation = query(async (id: string): Promise<Conversation | null> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const { results } = await DB.prepare(`
    SELECT * FROM conversations WHERE id = ? AND user_id = ?
  `).bind(id, userId).all();
  
  if (results.length === 0) return null;
  return mapConversationRow(results[0] as any);
}, "conversation"); // Dynamic key

// ============================================================================
// REPORT QUERIES
// ============================================================================

export const getReports = query(async (): Promise<Report[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const { results } = await DB.prepare(`
    SELECT * FROM reports 
    WHERE user_id = ?
    ORDER BY created_at DESC 
    LIMIT 50
  `).bind(userId).all();
  
  return (results as any[]).map(row => mapReportRow(row));
}, CACHE_KEYS.REPORTS);

export const getReport = query(async (id: string): Promise<Report | null> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const { results } = await DB.prepare(`
    SELECT * FROM reports WHERE id = ? AND user_id = ?
  `).bind(id, userId).all();
  
  if (results.length === 0) return null;
  return mapReportRow(results[0] as any);
}, "report"); // Dynamic key

// ============================================================================
// NOTIFICATION QUERIES
// ============================================================================

export const getNotifications = query(async (): Promise<Notification[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const { results } = await DB.prepare(`
    SELECT * FROM notifications 
    WHERE user_id = ?
    ORDER BY created_at DESC 
    LIMIT 100
  `).bind(userId).all();
  
  return (results as any[]).map(row => mapNotificationRow(row));
}, CACHE_KEYS.NOTIFICATIONS);

export const getUnreadNotificationCount = query(async (): Promise<number> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const { results } = await DB.prepare(`
    SELECT COUNT(*) as count FROM notifications WHERE is_read = 0 AND user_id = ?
  `).bind(userId).all();
  
  return (results[0] as any)?.count ?? 0;
}, CACHE_KEYS.UNREAD_COUNT);

// ============================================================================
// BRIEFING QUERIES
// ============================================================================

export const getTodaysBriefing = query(async (): Promise<Briefing> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  
  // Get recent predictions due
  const { results: duePredictions } = await DB.prepare(`
    SELECT p.*, v.title as video_title, c.name as channel_name
    FROM predictions p
    JOIN videos v ON v.id = p.video_id
    JOIN channels c ON c.id = p.channel_id
    WHERE p.status IN ('pending', 'due_soon')
      AND p.target_date IS NOT NULL
      AND date(p.target_date) <= date('now', '+7 days')
    ORDER BY p.target_date
    LIMIT 10
  `).all();
  
  // Get recently resolved predictions
  const { results: resolvedPredictions } = await DB.prepare(`
    SELECT p.*, v.title as video_title, c.name as channel_name
    FROM predictions p
    JOIN videos v ON v.id = p.video_id
    JOIN channels c ON c.id = p.channel_id
    WHERE p.status IN ('verified', 'failed')
      AND date(p.updated_at) >= date('now', '-7 days')
    ORDER BY p.updated_at DESC
    LIMIT 10
  `).all();
  
  return {
    date: new Date().toISOString().split("T")[0],
    topStories: [],
    consensusPoints: [],
    divergencePoints: [],
    predictionsDue: (duePredictions as any[]).map(row => mapPredictionRow(row)),
    predictionsResolved: (resolvedPredictions as any[]).map(row => mapPredictionRow(row)),
  };
}, CACHE_KEYS.TODAYS_BRIEFING);

// ============================================================================
// SEARCH QUERIES
// ============================================================================

export const searchVideos = query(async (queryStr: string): Promise<Video[]> => {
  "use server";
  
  await requireUserId();
  if (!queryStr) return [];

  const DB = requireDb();
  const searchTerm = `%${queryStr}%`;
  const { results } = await DB.prepare(`
    SELECT v.*, c.name as channel_name
    FROM videos v
    JOIN channels c ON c.id = v.channel_id
    WHERE v.triage_status = 'approved'
      AND (
        v.title LIKE ?1
        OR v.summary LIKE ?1
        OR json_extract(v.topic_tags, '$') LIKE ?1
      )
    ORDER BY v.published_at DESC
    LIMIT 50
  `).bind(searchTerm).all();
  
  return (results as any[]).map(row => mapVideoRow(row));
}, "search-videos"); // Dynamic key based on query

export const searchChunks = query(async (queryStr: string): Promise<TranscriptChunk[]> => {
  "use server";
  
  await requireUserId();
  if (!queryStr) return [];

  const DB = requireDb();
  const searchTerm = `%${queryStr}%`;
  const { results } = await DB.prepare(`
    SELECT tc.*
    FROM transcript_chunks tc
    JOIN videos v ON v.id = tc.video_id
    WHERE v.triage_status = 'approved'
      AND tc.text LIKE ?
    ORDER BY tc.created_at DESC
    LIMIT 100
  `).bind(searchTerm).all();
  
  return (results as any[]).map(row => mapTranscriptChunkRow(row));
}, "search-chunks"); // Dynamic key based on query

// ============================================================================
// SEMANTIC SEARCH QUERIES (Vector/Embedding-based)
// ============================================================================

export interface SemanticSearchResult {
  id: string;
  videoId: string;
  videoTitle: string;
  channelName: string;
  text: string;
  startTime: number;
  endTime: number;
  score: number;
}

export const semanticSearchVideos = query(async (queryStr: string, options?: {
  channelId?: string;
  topicTag?: string;
  limit?: number;
}): Promise<SemanticSearchResult[]> => {
  "use server";
  
  await requireUserId();
  if (!queryStr || queryStr.trim().length < 3) return [];
  
  try {
    const results = await ragSemanticSearch({
      query: queryStr,
      channelId: options?.channelId,
      topicTag: options?.topicTag,
      limit: options?.limit ?? 20,
    });
    
    return results;
  } catch (error) {
    console.error("[BFF] Semantic search error:", error);
    // Fallback to empty results on error (e.g., if AI binding unavailable)
    return [];
  }
}, "semantic-search-videos");

// ============================================================================
// VIDEO ANNOTATION QUERIES
// ============================================================================

export interface VideoAnnotation {
  id: string;
  videoId: string;
  chunkId?: string;
  timestamp?: number;
  text: string;
  type: "note" | "highlight" | "question" | "insight";
  createdAt: string;
}

export const getVideoAnnotations = query(async (videoId: string): Promise<VideoAnnotation[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT id, video_id, chunk_id, timestamp, text, type, created_at
    FROM video_annotations
    WHERE video_id = ?
    ORDER BY COALESCE(timestamp, 0), created_at
  `).bind(videoId).all();
  
  return (results as any[]).map(row => ({
    id: row.id as string,
    videoId: row.video_id as string,
    chunkId: row.chunk_id as string | undefined,
    timestamp: row.timestamp as number | undefined,
    text: row.text as string,
    type: row.type as "note" | "highlight" | "question" | "insight",
    createdAt: row.created_at as string,
  }));
}, "video-annotations");

// ============================================================================
// CHAT MESSAGE QUERIES
// ============================================================================

export interface ChatMessage {
  id: string;
  conversationId: string;
  role: "user" | "assistant";
  content: string;
  citations?: Array<{
    videoId: string;
    videoTitle: string;
    channelName: string;
    snippet: string;
    timestamp?: number;
  }>;
  createdAt: string;
}

export const getChatMessages = query(async (conversationId: string): Promise<ChatMessage[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const { results } = await DB.prepare(`
    SELECT id, conversation_id, role, content, citations, created_at
    FROM chat_messages
    WHERE conversation_id = ? AND user_id = ?
    ORDER BY created_at ASC
  `).bind(conversationId, userId).all();
  
  return (results as any[]).map(row => ({
    id: row.id as string,
    conversationId: row.conversation_id as string,
    role: row.role as "user" | "assistant",
    content: row.content as string,
    citations: row.citations ? JSON.parse(row.citations as string) : undefined,
    createdAt: row.created_at as string,
  }));
}, "chat-messages");

// ============================================================================
// SAVED SEARCH QUERIES
// ============================================================================

export interface SavedSearch {
  id: string;
  name: string;
  query: string;
  filters: Record<string, unknown>;
  alertsEnabled: boolean;
  lastRun?: string;
  resultCount?: number;
  createdAt: string;
}

export const getSavedSearches = query(async (): Promise<SavedSearch[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const { results } = await DB.prepare(`
    SELECT id, name, query, filters, alerts_enabled, last_run, result_count, created_at
    FROM saved_searches
    WHERE user_id = ?
    ORDER BY created_at DESC
  `).bind(userId).all();
  
  return (results as any[]).map(row => ({
    id: row.id as string,
    name: row.name as string,
    query: row.query as string,
    filters: row.filters ? JSON.parse(row.filters as string) : {},
    alertsEnabled: (row.alerts_enabled as number) === 1,
    lastRun: row.last_run as string | undefined,
    resultCount: row.result_count as number | undefined,
    createdAt: row.created_at as string,
  }));
}, "saved-searches");

// ============================================================================
// LIBRARY QUERIES (Approved videos for explore/library)
// ============================================================================

export interface LibraryFilters {
  channelId?: string;
  topicTag?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: "date" | "channel" | "relevance";
}

export const getLibraryVideos = query(async (filters?: LibraryFilters): Promise<Video[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  let sql = `
    SELECT v.*, c.name as channel_name
    FROM videos v
    JOIN channels c ON c.id = v.channel_id
    WHERE v.triage_status = 'approved'
  `;
  const binds: unknown[] = [];
  
  if (filters?.channelId) {
    sql += ` AND v.channel_id = ?`;
    binds.push(filters.channelId);
  }
  if (filters?.topicTag) {
    sql += ` AND EXISTS (SELECT 1 FROM json_each(v.topic_tags) WHERE value = ?)`;
    binds.push(filters.topicTag);
  }
  if (filters?.dateFrom) {
    sql += ` AND v.published_at >= ?`;
    binds.push(filters.dateFrom);
  }
  if (filters?.dateTo) {
    sql += ` AND v.published_at <= ?`;
    binds.push(filters.dateTo);
  }
  
  // Sort by
  switch (filters?.sortBy) {
    case "channel":
      sql += ` ORDER BY c.name, v.published_at DESC`;
      break;
    case "relevance":
      sql += ` ORDER BY v.priority DESC, v.published_at DESC`;
      break;
    default:
      sql += ` ORDER BY v.published_at DESC`;
  }
  
  sql += ` LIMIT 200`;
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  return (results as any[]).map(row => mapVideoRow(row));
}, "library-videos");

// ============================================================================
// CHANNEL VIDEO QUERIES
// ============================================================================

export const getChannelVideos = query(async (channelId: string): Promise<Video[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT v.*, c.name as channel_name
    FROM videos v
    JOIN channels c ON c.id = v.channel_id
    WHERE v.channel_id = ?
      AND v.triage_status = 'approved'
    ORDER BY v.published_at DESC
    LIMIT 100
  `).bind(channelId).all();
  
  return (results as any[]).map(row => mapVideoRow(row));
}, "channel-videos");

export const getChannelPredictions = query(async (channelId: string): Promise<Prediction[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT p.*, v.title as video_title, c.name as channel_name
    FROM predictions p
    JOIN videos v ON v.id = p.video_id
    JOIN channels c ON c.id = p.channel_id
    WHERE p.channel_id = ?
    ORDER BY p.created_at DESC
    LIMIT 50
  `).bind(channelId).all();
  
  return (results as any[]).map(row => mapPredictionRow(row));
}, "channel-predictions");

// ============================================================================
// TOPIC VIDEO QUERIES
// ============================================================================

export const getTopicVideos = query(async (topicId: string): Promise<Video[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  
  // First get the topic's keywords
  const { results: topicResults } = await DB.prepare(`
    SELECT name, keywords FROM topics WHERE id = ?
  `).bind(topicId).all();
  
  if (topicResults.length === 0) return [];
  
  const topicName = (topicResults[0] as any).name;
  const keywords = JSON.parse((topicResults[0] as any).keywords || "[]");
  
  // Search for videos matching topic name or keywords
  let sql = `
    SELECT DISTINCT v.*, c.name as channel_name
    FROM videos v
    JOIN channels c ON c.id = v.channel_id
    WHERE v.triage_status = 'approved'
      AND (
        EXISTS (SELECT 1 FROM json_each(v.topic_tags) WHERE value = ?)
  `;
  const binds: unknown[] = [topicName];
  
  // Add keyword conditions
  for (const keyword of keywords.slice(0, 5)) { // Limit to 5 keywords
    sql += ` OR v.title LIKE ? OR v.summary LIKE ?`;
    binds.push(`%${keyword}%`, `%${keyword}%`);
  }
  
  sql += `)
    ORDER BY v.published_at DESC
    LIMIT 100
  `;
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  return (results as any[]).map(row => mapVideoRow(row));
}, "topic-videos");

export const getTopicPredictions = query(async (topicId: string): Promise<Prediction[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  
  // First get the topic name
  const { results: topicResults } = await DB.prepare(`
    SELECT name FROM topics WHERE id = ?
  `).bind(topicId).all();
  
  if (topicResults.length === 0) return [];
  
  const topicName = (topicResults[0] as any).name;
  
  const { results } = await DB.prepare(`
    SELECT p.*, v.title as video_title, c.name as channel_name
    FROM predictions p
    JOIN videos v ON v.id = p.video_id
    JOIN channels c ON c.id = p.channel_id
    WHERE EXISTS (SELECT 1 FROM json_each(p.topic_tags) WHERE value = ?)
    ORDER BY p.target_date ASC NULLS LAST, p.created_at DESC
    LIMIT 50
  `).bind(topicName).all();
  
  return (results as any[]).map(row => mapPredictionRow(row));
}, "topic-predictions");

// ============================================================================
// DASHBOARD/HOME QUERIES
// ============================================================================

export interface DashboardStats {
  inboxCount: number;
  flashCount: number;
  predictionsActive: number;
  predictionsDue: number;
  videosThisWeek: number;
  topicsActive: number;
}

export const getDashboardStats = query(async (): Promise<DashboardStats> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  
  // Get inbox counts
  const { results: inboxResults } = await DB.prepare(`
    SELECT 
      COUNT(*) as total,
      SUM(CASE WHEN priority = 'flash' THEN 1 ELSE 0 END) as flash
    FROM videos WHERE triage_status = 'pending'
  `).all();
  
  // Get prediction counts
  const { results: predResults } = await DB.prepare(`
    SELECT 
      COUNT(*) as active,
      SUM(CASE WHEN target_date IS NOT NULL 
        AND date(target_date) <= date('now', '+7 days') 
        AND status = 'pending' THEN 1 ELSE 0 END) as due_soon
    FROM predictions WHERE status IN ('pending', 'due_soon')
  `).all();
  
  // Get videos this week
  const { results: videoResults } = await DB.prepare(`
    SELECT COUNT(*) as count
    FROM videos 
    WHERE triage_status = 'approved'
      AND date(published_at) >= date('now', '-7 days')
  `).all();
  
  // Get active topics
  const { results: topicResults } = await DB.prepare(`
    SELECT COUNT(*) as count FROM topics WHERE recent_activity > 0
  `).all();
  
  const inbox = inboxResults[0] as any;
  const preds = predResults[0] as any;
  const videos = videoResults[0] as any;
  const topics = topicResults[0] as any;
  
  return {
    inboxCount: inbox?.total ?? 0,
    flashCount: inbox?.flash ?? 0,
    predictionsActive: preds?.active ?? 0,
    predictionsDue: preds?.due_soon ?? 0,
    videosThisWeek: videos?.count ?? 0,
    topicsActive: topics?.count ?? 0,
  };
}, "dashboard-stats");

export const getRecentActivity = query(async (): Promise<Array<{
  type: "video" | "prediction" | "analysis" | "report";
  id: string;
  title: string;
  subtitle?: string;
  timestamp: string;
}>> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  
  // Get recent videos
  const { results: videoResults } = await DB.prepare(`
    SELECT v.id, v.title, c.name as channel_name, v.created_at
    FROM videos v
    JOIN channels c ON c.id = v.channel_id
    WHERE v.triage_status = 'approved'
    ORDER BY v.created_at DESC
    LIMIT 5
  `).all();
  
  // Get recent predictions
  const { results: predResults } = await DB.prepare(`
    SELECT p.id, p.event as title, c.name as channel_name, p.created_at
    FROM predictions p
    JOIN channels c ON c.id = p.channel_id
    ORDER BY p.created_at DESC
    LIMIT 5
  `).all();
  
  // Get recent analysis runs
  const { results: analysisResults } = await DB.prepare(`
    SELECT id, name as title, template_name as subtitle, created_at
    FROM analysis_runs
    WHERE status = 'completed'
    ORDER BY created_at DESC
    LIMIT 3
  `).all();
  
  // Get recent reports
  const { results: reportResults } = await DB.prepare(`
    SELECT id, name as title, type as subtitle, created_at
    FROM reports
    WHERE status = 'completed'
    ORDER BY created_at DESC
    LIMIT 3
  `).all();
  
  // Combine and sort
  const activities: Array<{
    type: "video" | "prediction" | "analysis" | "report";
    id: string;
    title: string;
    subtitle?: string;
    timestamp: string;
  }> = [];
  
  for (const v of videoResults as any[]) {
    activities.push({
      type: "video",
      id: v.id,
      title: v.title,
      subtitle: v.channel_name,
      timestamp: v.created_at,
    });
  }
  
  for (const p of predResults as any[]) {
    activities.push({
      type: "prediction",
      id: p.id,
      title: p.title,
      subtitle: p.channel_name,
      timestamp: p.created_at,
    });
  }
  
  for (const a of analysisResults as any[]) {
    activities.push({
      type: "analysis",
      id: a.id,
      title: a.title,
      subtitle: a.subtitle,
      timestamp: a.created_at,
    });
  }
  
  for (const r of reportResults as any[]) {
    activities.push({
      type: "report",
      id: r.id,
      title: r.title,
      subtitle: r.subtitle,
      timestamp: r.created_at,
    });
  }
  
  // Sort by timestamp descending and return top 10
  return activities
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 10);
}, "recent-activity");

// ============================================================================
// USER SETTINGS QUERIES
// ============================================================================

export interface UserSettings {
  id: string;
  theme: "light" | "dark" | "system";
  timezone: string;
  dateFormat: string;
  timeFormat: "12h" | "24h";
  notificationPreferences: {
    content?: { inApp?: boolean; email?: boolean };
    predictions?: { inApp?: boolean; email?: boolean };
    reports?: { inApp?: boolean; email?: boolean };
    system?: { inApp?: boolean; email?: boolean };
  };
  quietHoursStart?: string;
  quietHoursEnd?: string;
  updatedAt?: string;
}

export const getUserSettings = query(async (): Promise<UserSettings> => {
  "use server";
  
  const DB = requireDb();
  const settingsUserId = await requireUserId();
  
  const { results } = await DB.prepare(`
    SELECT id, theme, timezone, date_format, time_format, 
           notification_preferences, quiet_hours_start, quiet_hours_end, updated_at
    FROM user_settings
    WHERE user_id = ?
  `).bind(settingsUserId).all();
  
  if (results.length === 0) {
    // Return defaults if no settings exist
    return {
      id: settingsUserId,
      theme: "system",
      timezone: "America/New_York",
      dateFormat: "MMM D, YYYY",
      timeFormat: "12h",
      notificationPreferences: {},
      quietHoursStart: "22:00",
      quietHoursEnd: "08:00",
    };
  }
  
  const row = results[0] as any;
  return {
    id: row.id,
    theme: row.theme ?? "system",
    timezone: row.timezone ?? "America/New_York",
    dateFormat: row.date_format ?? "MMM D, YYYY",
    timeFormat: row.time_format ?? "12h",
    notificationPreferences: row.notification_preferences 
      ? JSON.parse(row.notification_preferences) 
      : {},
    quietHoursStart: row.quiet_hours_start,
    quietHoursEnd: row.quiet_hours_end,
    updatedAt: row.updated_at,
  };
}, CACHE_KEYS.USER_SETTINGS);

// ============================================================================
// AUTOMATION QUERIES
// ============================================================================

export interface AutomationRule {
  id: string;
  name: string;
  conditions: Array<{
    field: string;
    operator: string;
    value: string;
  }>;
  action: "approve" | "archive" | "snooze_1d" | "snooze_1w" | "set_priority_high" | "set_priority_low";
  dailyLimit: number;
  actionsToday: number;
  enabled: boolean;
  createdAt: string;
  updatedAt?: string;
}

export const getAutomationRules = query(async (): Promise<AutomationRule[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  
  const { results } = await DB.prepare(`
    SELECT id, name, conditions, action, daily_limit, actions_today, enabled, created_at, updated_at
    FROM automation_rules
    WHERE user_id = ?
    ORDER BY created_at DESC
  `).bind(userId).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    name: row.name,
    conditions: row.conditions ? JSON.parse(row.conditions) : [],
    action: row.action,
    dailyLimit: row.daily_limit ?? 50,
    actionsToday: row.actions_today ?? 0,
    enabled: row.enabled === 1,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}, CACHE_KEYS.AUTOMATION_RULES);

export interface AutomationLogEntry {
  id: string;
  ruleId: string;
  ruleName?: string;
  videoId: string;
  videoTitle?: string;
  actionTaken: string;
  result: "success" | "error";
  details?: string;
  createdAt: string;
}

export const getAutomationLog = query(async (options?: { 
  limit?: number; 
  ruleId?: string;
}): Promise<AutomationLogEntry[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  
  let sql = `
    SELECT al.id, al.rule_id, ar.name as rule_name, al.video_id, v.title as video_title,
           al.action_taken, al.result, al.details, al.created_at
    FROM automation_log al
    LEFT JOIN automation_rules ar ON ar.id = al.rule_id
    LEFT JOIN videos v ON v.id = al.video_id
    WHERE al.user_id = ?
  `;
  const binds: unknown[] = [userId];
  
  if (options?.ruleId) {
    sql += ` AND al.rule_id = ?`;
    binds.push(options.ruleId);
  }
  
  sql += ` ORDER BY al.created_at DESC LIMIT ?`;
  binds.push(options?.limit ?? 100);
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    ruleId: row.rule_id,
    ruleName: row.rule_name,
    videoId: row.video_id,
    videoTitle: row.video_title,
    actionTaken: row.action_taken,
    result: row.result,
    details: row.details,
    createdAt: row.created_at,
  }));
}, CACHE_KEYS.AUTOMATION_LOG);

// ============================================================================
// CREDIBILITY QUERIES (v4.1)
// ============================================================================

export interface CredibilityBreakdown {
  accuracy: number;      // 0-100: % of verified predictions
  calibration: number;   // 0-100: how well confidence matches outcomes
  consistency: number;   // 0-100: prediction frequency and quality over time
  volume: number;        // 0-100: number of predictions (more = higher confidence in score)
}

export interface CredibilityScore {
  channelId: string;
  channelName: string;
  score: number | null;        // 0-100 composite score, null if insufficient data
  breakdown: CredibilityBreakdown | null;
  override: {
    adjustment: number;
    reason?: string;
  } | null;
  effectiveScore: number | null; // score + override adjustment, clamped to 0-100
  predictionCount: number;
  resolvedCount: number;
  updatedAt: string | null;
}

export const getCredibilityScore = query(async (channelId: string): Promise<CredibilityScore> => {
  "use server";
  
  const DB = requireDb();
  
  // Get channel with credibility data
  const { results: channelResults } = await DB.prepare(`
    SELECT c.id, c.name, c.credibility_score, c.credibility_breakdown, c.credibility_updated_at,
           (SELECT COUNT(*) FROM predictions WHERE channel_id = c.id) as prediction_count,
           (SELECT COUNT(*) FROM predictions WHERE channel_id = c.id AND status IN ('verified', 'failed')) as resolved_count
    FROM channels c
    WHERE c.id = ?
  `).bind(channelId).all();
  
  if (channelResults.length === 0) {
    throw new Error("Channel not found");
  }
  
  const channel = channelResults[0] as any;
  
  // Get user override if any
  const userId = await requireUserId();
  const { results: overrideResults } = await DB.prepare(`
    SELECT adjustment, reason FROM credibility_overrides
    WHERE channel_id = ? AND user_id = ?
  `).bind(channelId, userId).all();
  
  const override = overrideResults.length > 0 ? {
    adjustment: (overrideResults[0] as any).adjustment as number,
    reason: (overrideResults[0] as any).reason as string | undefined,
  } : null;
  
  const score = channel.credibility_score as number | null;
  const breakdown = channel.credibility_breakdown 
    ? JSON.parse(channel.credibility_breakdown) as CredibilityBreakdown
    : null;
  
  // Calculate effective score with override
  let effectiveScore: number | null = null;
  if (score !== null) {
    effectiveScore = Math.max(0, Math.min(100, score + (override?.adjustment ?? 0)));
  }
  
  return {
    channelId: channel.id,
    channelName: channel.name,
    score,
    breakdown,
    override,
    effectiveScore,
    predictionCount: channel.prediction_count ?? 0,
    resolvedCount: channel.resolved_count ?? 0,
    updatedAt: channel.credibility_updated_at,
  };
}, "credibility-score");

export interface LeaderboardEntry {
  channelId: string;
  channelName: string;
  channelHandle?: string;
  thumbnailUrl?: string;
  score: number;
  effectiveScore: number;
  predictionCount: number;
  resolvedCount: number;
  verifiedCount: number;
  accuracy: number;
  rank: number;
}

export interface LeaderboardFilters {
  topicTag?: string;
  minPredictions?: number;
  limit?: number;
}

export const getCredibilityLeaderboard = query(async (filters?: LeaderboardFilters): Promise<LeaderboardEntry[]> => {
  "use server";
  
  const DB = requireDb();
  const minPredictions = filters?.minPredictions ?? 3;
  const limit = filters?.limit ?? 50;
  
  let sql = `
    SELECT c.id, c.name, c.handle, c.thumbnail_url, 
           c.credibility_score,
           (SELECT COUNT(*) FROM predictions p WHERE p.channel_id = c.id) as prediction_count,
           (SELECT COUNT(*) FROM predictions p WHERE p.channel_id = c.id AND p.status IN ('verified', 'failed')) as resolved_count,
           (SELECT COUNT(*) FROM predictions p WHERE p.channel_id = c.id AND p.status = 'verified') as verified_count,
           COALESCE((SELECT adjustment FROM credibility_overrides co WHERE co.channel_id = c.id AND co.user_id = ?), 0) as override_adjustment
    FROM channels c
    WHERE c.credibility_score IS NOT NULL
  `;
  const userId = await requireUserId();
  const binds: unknown[] = [userId];
  
  if (filters?.topicTag) {
    sql += `
      AND c.id IN (
        SELECT DISTINCT p.channel_id FROM predictions p 
        WHERE EXISTS (SELECT 1 FROM json_each(p.topic_tags) WHERE value = ?)
      )
    `;
    binds.push(filters.topicTag);
  }
  
  sql += `
    AND (SELECT COUNT(*) FROM predictions WHERE channel_id = c.id AND status IN ('verified', 'failed')) >= ?
    ORDER BY (c.credibility_score + COALESCE((SELECT adjustment FROM credibility_overrides co WHERE co.channel_id = c.id AND co.user_id = ?), 0)) DESC
    LIMIT ?
  `;
  binds.push(minPredictions, userId, limit);
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  
  return (results as any[]).map((row, index) => {
    const score = row.credibility_score as number;
    const overrideAdjustment = row.override_adjustment as number;
    const resolvedCount = row.resolved_count as number;
    const verifiedCount = row.verified_count as number;
    
    return {
      channelId: row.id,
      channelName: row.name,
      channelHandle: row.handle,
      thumbnailUrl: row.thumbnail_url,
      score,
      effectiveScore: Math.max(0, Math.min(100, score + overrideAdjustment)),
      predictionCount: row.prediction_count,
      resolvedCount,
      verifiedCount,
      accuracy: resolvedCount > 0 ? Math.round((verifiedCount / resolvedCount) * 100) : 0,
      rank: index + 1,
    };
  });
}, CACHE_KEYS.CREDIBILITY_LEADERBOARD);

export interface TopicAccuracy {
  topic: string;
  totalPredictions: number;
  resolvedPredictions: number;
  verifiedCount: number;
  failedCount: number;
  accuracy: number; // 0-100
}

export const getCreatorTopicAccuracy = query(async (channelId: string): Promise<TopicAccuracy[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  
  // Get all predictions for this channel
  const { results } = await DB.prepare(`
    SELECT topic_tags, status FROM predictions
    WHERE channel_id = ? AND status IN ('verified', 'failed')
  `).bind(channelId).all();
  
  // Aggregate by topic
  const topicMap = new Map<string, { verified: number; failed: number; total: number }>();
  
  for (const row of results as any[]) {
    const topics: string[] = row.topic_tags ? JSON.parse(row.topic_tags) : [];
    const status = row.status as string;
    
    for (const topic of topics) {
      if (!topicMap.has(topic)) {
        topicMap.set(topic, { verified: 0, failed: 0, total: 0 });
      }
      const entry = topicMap.get(topic)!;
      entry.total++;
      if (status === "verified") entry.verified++;
      if (status === "failed") entry.failed++;
    }
  }
  
  // Also get pending/due predictions for total count
  const { results: allResults } = await DB.prepare(`
    SELECT topic_tags FROM predictions WHERE channel_id = ?
  `).bind(channelId).all();
  
  const allTopicCounts = new Map<string, number>();
  for (const row of allResults as any[]) {
    const topics: string[] = row.topic_tags ? JSON.parse(row.topic_tags) : [];
    for (const topic of topics) {
      allTopicCounts.set(topic, (allTopicCounts.get(topic) ?? 0) + 1);
    }
  }
  
  // Build result
  const topicAccuracies: TopicAccuracy[] = [];
  for (const [topic, counts] of topicMap.entries()) {
    topicAccuracies.push({
      topic,
      totalPredictions: allTopicCounts.get(topic) ?? counts.total,
      resolvedPredictions: counts.total,
      verifiedCount: counts.verified,
      failedCount: counts.failed,
      accuracy: counts.total > 0 ? Math.round((counts.verified / counts.total) * 100) : 0,
    });
  }
  
  // Sort by total predictions descending
  return topicAccuracies.sort((a, b) => b.totalPredictions - a.totalPredictions);
}, "topic-accuracy");

export interface CredibilityHistoryEntry {
  score: number;
  breakdown: CredibilityBreakdown;
  recordedAt: string;
}

export const getCredibilityHistory = query(async (channelId: string, options?: {
  limit?: number;
  since?: string;
}): Promise<CredibilityHistoryEntry[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const limit = options?.limit ?? 30;
  
  let sql = `
    SELECT score, breakdown, recorded_at
    FROM credibility_history
    WHERE channel_id = ?
  `;
  const binds: unknown[] = [channelId];
  
  if (options?.since) {
    sql += ` AND recorded_at >= ?`;
    binds.push(options.since);
  }
  
  sql += ` ORDER BY recorded_at DESC LIMIT ?`;
  binds.push(limit);
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  
  return (results as any[]).map(row => ({
    score: row.score,
    breakdown: JSON.parse(row.breakdown),
    recordedAt: row.recorded_at,
  }));
}, "credibility-history");

// ============================================================================
// SEARCH ALERT QUERIES (v4.1)
// ============================================================================

export interface SearchAlertHistoryEntry {
  id: string;
  searchId: string;
  searchName?: string;
  newResultsCount: number;
  videoIds: string[];
  triggeredAt: string;
}

export const getSearchAlertHistory = query(async (searchId: string, options?: {
  limit?: number;
}): Promise<SearchAlertHistoryEntry[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const limit = options?.limit ?? 50;
  
  const { results } = await DB.prepare(`
    SELECT sah.id, sah.search_id, ss.name as search_name, 
           sah.new_results_count, sah.video_ids, sah.triggered_at
    FROM search_alert_history sah
    JOIN saved_searches ss ON ss.id = sah.search_id
    WHERE sah.search_id = ?
      AND ss.user_id = ?
    ORDER BY sah.triggered_at DESC
    LIMIT ?
  `).bind(searchId, userId, limit).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    searchId: row.search_id,
    searchName: row.search_name,
    newResultsCount: row.new_results_count,
    videoIds: row.video_ids ? JSON.parse(row.video_ids) : [],
    triggeredAt: row.triggered_at,
  }));
}, "search-alert-history");

// ============================================================================
// STORAGE STATS QUERIES (for /settings/data)
// ============================================================================

export interface StorageStats {
  videosCount: number;
  videosSize: number; // Estimated in bytes
  transcriptChunksCount: number;
  predictionsCount: number;
  dossiersCount: number;
  conversationsCount: number;
  reportsCount: number;
  savedSearchesCount: number;
  annotationsCount: number;
  totalEstimatedSize: number;
}

export const getStorageStats = query(async (): Promise<StorageStats> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  
  // Get counts from all tables
  const countQueries = await Promise.all([
    DB.prepare(`SELECT COUNT(*) as count FROM videos`).all(),
    DB.prepare(`SELECT COUNT(*) as count, SUM(LENGTH(text)) as size FROM transcript_chunks`).all(),
    DB.prepare(`SELECT COUNT(*) as count FROM predictions`).all(),
    DB.prepare(`SELECT COUNT(*) as count FROM dossiers WHERE user_id = ?`).bind(userId).all(),
    DB.prepare(`SELECT COUNT(*) as count FROM conversations WHERE user_id = ?`).bind(userId).all(),
    DB.prepare(`SELECT COUNT(*) as count FROM reports WHERE user_id = ?`).bind(userId).all(),
    DB.prepare(`SELECT COUNT(*) as count FROM saved_searches WHERE user_id = ?`).bind(userId).all(),
    DB.prepare(`SELECT COUNT(*) as count FROM video_annotations`).all(),
  ]);
  
  const videosCount = (countQueries[0].results[0] as any)?.count ?? 0;
  const chunksData = countQueries[1].results[0] as any;
  const transcriptChunksCount = chunksData?.count ?? 0;
  const transcriptSize = chunksData?.size ?? 0;
  const predictionsCount = (countQueries[2].results[0] as any)?.count ?? 0;
  const dossiersCount = (countQueries[3].results[0] as any)?.count ?? 0;
  const conversationsCount = (countQueries[4].results[0] as any)?.count ?? 0;
  const reportsCount = (countQueries[5].results[0] as any)?.count ?? 0;
  const savedSearchesCount = (countQueries[6].results[0] as any)?.count ?? 0;
  const annotationsCount = (countQueries[7].results[0] as any)?.count ?? 0;
  
  // Estimate video metadata size (avg ~2KB per video)
  const videosSize = videosCount * 2048;
  
  // Total estimated size
  const totalEstimatedSize = videosSize + transcriptSize + (predictionsCount * 512) + 
    (dossiersCount * 1024) + (conversationsCount * 4096) + (reportsCount * 8192);
  
  return {
    videosCount,
    videosSize,
    transcriptChunksCount,
    predictionsCount,
    dossiersCount,
    conversationsCount,
    reportsCount,
    savedSearchesCount,
    annotationsCount,
    totalEstimatedSize,
  };
}, CACHE_KEYS.STORAGE_STATS);

// ============================================================================
// TOPIC DISCOVERY QUERIES (v4.2)
// ============================================================================

export interface DiscoveredVideo {
  id: string;
  topicId: string;
  videoId: string;  // YouTube video ID
  channelId?: string;
  channelTitle?: string;
  channelHandle?: string;
  title: string;
  description?: string;
  publishedAt?: string;
  thumbnailUrl?: string;
  relevanceScore?: number;
  status: "pending" | "approved" | "dismissed";
  discoveredAt: string;
  actionedAt?: string;
}

export interface DiscoveredVideoFilters {
  status?: "pending" | "approved" | "dismissed";
  limit?: number;
}

export const getDiscoveredVideos = query(async (topicId: string, filters?: DiscoveredVideoFilters): Promise<DiscoveredVideo[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const limit = filters?.limit ?? 50;
  
  let sql = `
    SELECT id, topic_id, video_id, channel_id, channel_title, channel_handle,
           title, description, published_at, thumbnail_url, relevance_score,
           status, discovered_at, actioned_at
    FROM discovered_videos
    WHERE topic_id = ?
  `;
  const binds: unknown[] = [topicId];
  
  if (filters?.status) {
    sql += ` AND status = ?`;
    binds.push(filters.status);
  }
  
  sql += ` ORDER BY relevance_score DESC, discovered_at DESC LIMIT ?`;
  binds.push(limit);
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    topicId: row.topic_id,
    videoId: row.video_id,
    channelId: row.channel_id,
    channelTitle: row.channel_title,
    channelHandle: row.channel_handle,
    title: row.title,
    description: row.description,
    publishedAt: row.published_at,
    thumbnailUrl: row.thumbnail_url,
    relevanceScore: row.relevance_score,
    status: row.status,
    discoveredAt: row.discovered_at,
    actionedAt: row.actioned_at,
  }));
}, "discovered-videos");

export interface RecommendedChannel {
  youtubeChannelId: string;
  channelTitle: string;
  channelHandle?: string;
  thumbnailUrl?: string;
  videoCount: number;
  topicOverlap: string[];  // Topics this channel covers
  relevanceScore: number;
}

export const getRecommendedChannels = query(async (topicId?: string): Promise<RecommendedChannel[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  
  // Get channels from discovered videos that are not already monitored
  let sql = `
    SELECT dv.channel_id, dv.channel_title, dv.channel_handle,
           COUNT(DISTINCT dv.id) as video_count,
           GROUP_CONCAT(DISTINCT t.name) as topics,
           AVG(dv.relevance_score) as avg_relevance
    FROM discovered_videos dv
    JOIN topics t ON t.id = dv.topic_id
    WHERE dv.channel_id IS NOT NULL
      AND dv.channel_id NOT IN (SELECT youtube_channel_id FROM channels WHERE is_monitored = 1)
      AND dv.channel_id NOT IN (SELECT youtube_channel_id FROM blocked_channels)
  `;
  const binds: unknown[] = [];
  
  if (topicId) {
    sql += ` AND dv.topic_id = ?`;
    binds.push(topicId);
  }
  
  sql += `
    GROUP BY dv.channel_id
    HAVING video_count >= 2
    ORDER BY avg_relevance DESC, video_count DESC
    LIMIT 20
  `;
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  
  return (results as any[]).map(row => ({
    youtubeChannelId: row.channel_id,
    channelTitle: row.channel_title,
    channelHandle: row.channel_handle,
    thumbnailUrl: undefined,
    videoCount: row.video_count,
    topicOverlap: row.topics ? row.topics.split(",") : [],
    relevanceScore: row.avg_relevance ?? 0,
  }));
}, CACHE_KEYS.RECOMMENDED_CHANNELS);

export interface BlockedChannel {
  id: string;
  youtubeChannelId: string;
  channelTitle?: string;
  reason?: string;
  blockedAt: string;
}

export const getBlockedChannels = query(async (): Promise<BlockedChannel[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT id, youtube_channel_id, channel_title, reason, blocked_at
    FROM blocked_channels
    ORDER BY blocked_at DESC
  `).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    youtubeChannelId: row.youtube_channel_id,
    channelTitle: row.channel_title,
    reason: row.reason,
    blockedAt: row.blocked_at,
  }));
}, CACHE_KEYS.BLOCKED_CHANNELS);

// ============================================================================
// DOSSIER TIMELINE QUERIES (v4.2)
// ============================================================================

export interface TimelineItem {
  id: string;
  sectionId: string;
  sectionName: string;
  type: string;
  content: string;
  metadata?: Record<string, unknown>;
  eventDate: string | null;
  eventDatePrecision: "year" | "month" | "day" | "time";
  eventDateSource: "auto" | "manual";
  order: number;
  createdAt: string;
}

export interface TimelineFilters {
  itemTypes?: string[];
  dateRange?: { from: string; to: string };
}

export const getDossierTimeline = query(async (dossierId: string, filters?: TimelineFilters): Promise<{
  dated: TimelineItem[];
  undated: TimelineItem[];
}> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  
  let sql = `
    SELECT di.id, di.section_id, ds.name as section_name, di.item_type, di.content,
           di.metadata, di.event_date, di.event_date_precision, di.event_date_source,
           di.sort_order, di.created_at
    FROM dossier_items di
    JOIN dossier_sections ds ON ds.id = di.section_id
    JOIN dossiers d ON d.id = ds.dossier_id
    WHERE ds.dossier_id = ? AND d.user_id = ?
  `;
  const binds: unknown[] = [dossierId, userId];
  
  if (filters?.itemTypes && filters.itemTypes.length > 0) {
    const placeholders = filters.itemTypes.map(() => "?").join(",");
    sql += ` AND di.item_type IN (${placeholders})`;
    binds.push(...filters.itemTypes);
  }
  
  if (filters?.dateRange?.from) {
    sql += ` AND (di.event_date IS NULL OR di.event_date >= ?)`;
    binds.push(filters.dateRange.from);
  }
  
  if (filters?.dateRange?.to) {
    sql += ` AND (di.event_date IS NULL OR di.event_date <= ?)`;
    binds.push(filters.dateRange.to);
  }
  
  sql += ` ORDER BY di.event_date ASC NULLS LAST, di.sort_order`;
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  
  const items: TimelineItem[] = (results as any[]).map(row => ({
    id: row.id,
    sectionId: row.section_id,
    sectionName: row.section_name,
    type: row.item_type,
    content: row.content,
    metadata: row.metadata ? JSON.parse(row.metadata) : undefined,
    eventDate: row.event_date,
    eventDatePrecision: row.event_date_precision ?? "day",
    eventDateSource: row.event_date_source ?? "auto",
    order: row.sort_order,
    createdAt: row.created_at,
  }));
  
  return {
    dated: items.filter(i => i.eventDate !== null),
    undated: items.filter(i => i.eventDate === null),
  };
}, "dossier-timeline");

export interface ItemRelationship {
  id: string;
  dossierId: string;
  sourceItemId: string;
  targetItemId: string;
  relationshipType: "supports" | "contradicts" | "related" | "follows";
  notes?: string;
  createdAt: string;
}

export const getDossierRelationships = query(async (dossierId: string): Promise<ItemRelationship[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const { results } = await DB.prepare(`
    SELECT id, dossier_id, source_item_id, target_item_id, relationship_type, notes, created_at
    FROM dossier_item_relationships
    WHERE dossier_id = ? AND user_id = ?
    ORDER BY created_at
  `).bind(dossierId, userId).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    dossierId: row.dossier_id,
    sourceItemId: row.source_item_id,
    targetItemId: row.target_item_id,
    relationshipType: row.relationship_type,
    notes: row.notes,
    createdAt: row.created_at,
  }));
}, "dossier-relationships");

// ============================================================================
// DOSSIER ITEMS QUERY (for RelationshipEditor)
// ============================================================================

export interface DossierItem {
  id: string;
  sectionId: string;
  title: string;
  type: string;
  eventDate?: string;
}

export const getDossierItems = query(async (dossierId: string): Promise<DossierItem[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const { results } = await DB.prepare(`
    SELECT di.id, di.section_id, 
           COALESCE(json_extract(di.metadata, '$.title'), SUBSTR(di.content, 1, 100)) as title,
           di.item_type, di.event_date
    FROM dossier_items di
    JOIN dossier_sections ds ON ds.id = di.section_id
    JOIN dossiers d ON d.id = ds.dossier_id
    WHERE ds.dossier_id = ? AND d.user_id = ?
    ORDER BY di.sort_order
  `).bind(dossierId, userId).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    sectionId: row.section_id,
    title: row.title ?? "Untitled",
    type: row.item_type,
    eventDate: row.event_date,
  }));
}, "dossier-items");

// ============================================================================
// ENTITY QUERIES (v4.3)
// ============================================================================

export interface Entity {
  id: string;
  type: "person" | "company" | "product" | "event" | "location";
  name: string;
  canonicalName?: string;
  aliases: string[];
  externalIds?: Record<string, string>;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface VideoEntity {
  entity: Entity;
  mentionCount: number;
  firstMentionTime?: number;
  confidence: number;
}

export const getVideoEntities = query(async (videoId: string): Promise<VideoEntity[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT e.id, e.type, e.name, e.canonical_name, e.aliases, e.external_ids, e.metadata, e.created_at,
           ve.mention_count, ve.first_mention_time, ve.confidence
    FROM video_entities ve
    JOIN entities e ON e.id = ve.entity_id
    WHERE ve.video_id = ?
    ORDER BY ve.mention_count DESC, e.name
  `).bind(videoId).all();
  
  return (results as any[]).map(row => ({
    entity: {
      id: row.id,
      type: row.type,
      name: row.name,
      canonicalName: row.canonical_name,
      aliases: row.aliases ? JSON.parse(row.aliases) : [],
      externalIds: row.external_ids ? JSON.parse(row.external_ids) : undefined,
      metadata: row.metadata ? JSON.parse(row.metadata) : undefined,
      createdAt: row.created_at,
    },
    mentionCount: row.mention_count ?? 1,
    firstMentionTime: row.first_mention_time,
    confidence: row.confidence ?? 1.0,
  }));
}, "video-entities");

export interface EntityMention {
  videoId: string;
  videoTitle: string;
  channelName: string;
  publishedAt: string;
  mentionCount: number;
  firstMentionTime?: number;
  chunks: Array<{
    chunkId: string;
    text: string;
    startTime: number;
    endTime: number;
  }>;
}

export interface EntityMentionFilters {
  dateRange?: { from: string; to: string };
  channelIds?: string[];
  limit?: number;
}

export const getEntityMentions = query(async (entityId: string, filters?: EntityMentionFilters): Promise<EntityMention[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const limit = filters?.limit ?? 50;
  
  let sql = `
    SELECT ve.video_id, v.title, c.name as channel_name, v.published_at,
           ve.mention_count, ve.first_mention_time
    FROM video_entities ve
    JOIN videos v ON v.id = ve.video_id
    JOIN channels c ON c.id = v.channel_id
    WHERE ve.entity_id = ?
  `;
  const binds: unknown[] = [entityId];
  
  if (filters?.dateRange?.from) {
    sql += ` AND v.published_at >= ?`;
    binds.push(filters.dateRange.from);
  }
  if (filters?.dateRange?.to) {
    sql += ` AND v.published_at <= ?`;
    binds.push(filters.dateRange.to);
  }
  if (filters?.channelIds && filters.channelIds.length > 0) {
    const placeholders = filters.channelIds.map(() => "?").join(",");
    sql += ` AND v.channel_id IN (${placeholders})`;
    binds.push(...filters.channelIds);
  }
  
  sql += ` ORDER BY v.published_at DESC LIMIT ?`;
  binds.push(limit);
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  
  // Get chunk-level mentions for each video
  const mentions: EntityMention[] = [];
  for (const row of results as any[]) {
    const { results: chunkResults } = await DB.prepare(`
      SELECT tc.id, tc.text, tc.start_time, tc.end_time
      FROM chunk_entities ce
      JOIN transcript_chunks tc ON tc.id = ce.chunk_id
      WHERE ce.entity_id = ? AND tc.video_id = ?
      ORDER BY tc.start_time
      LIMIT 5
    `).bind(entityId, row.video_id).all();
    
    mentions.push({
      videoId: row.video_id,
      videoTitle: row.title,
      channelName: row.channel_name,
      publishedAt: row.published_at,
      mentionCount: row.mention_count ?? 1,
      firstMentionTime: row.first_mention_time,
      chunks: (chunkResults as any[]).map(chunk => ({
        chunkId: chunk.id,
        text: chunk.text,
        startTime: chunk.start_time,
        endTime: chunk.end_time,
      })),
    });
  }
  
  return mentions;
}, "entity-mentions");

export interface EntitySearchFilters {
  type?: "person" | "company" | "product" | "event" | "location";
  limit?: number;
}

export const searchEntities = query(async (queryStr: string, filters?: EntitySearchFilters): Promise<Entity[]> => {
  "use server";
  
  await requireUserId();
  if (!queryStr || queryStr.length < 2) return [];
  
  const DB = requireDb();
  const limit = filters?.limit ?? 20;
  const searchTerm = `%${queryStr}%`;
  
  let sql = `
    SELECT id, type, name, canonical_name, aliases, external_ids, metadata, created_at
    FROM entities
    WHERE (name LIKE ? OR canonical_name LIKE ? OR aliases LIKE ?)
  `;
  const binds: unknown[] = [searchTerm, searchTerm, searchTerm];
  
  if (filters?.type) {
    sql += ` AND type = ?`;
    binds.push(filters.type);
  }
  
  sql += ` ORDER BY name LIMIT ?`;
  binds.push(limit);
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    type: row.type,
    name: row.name,
    canonicalName: row.canonical_name,
    aliases: row.aliases ? JSON.parse(row.aliases) : [],
    externalIds: row.external_ids ? JSON.parse(row.external_ids) : undefined,
    metadata: row.metadata ? JSON.parse(row.metadata) : undefined,
    createdAt: row.created_at,
  }));
}, "entity-search");

export interface SuggestedTag {
  id: string;
  videoId: string;
  tag: string;
  reason: "entity_cluster" | "topic_match" | "ai_suggestion";
  confidence: number;
  status: "pending" | "accepted" | "rejected";
  createdAt: string;
  actionedAt?: string;
}

export const getSuggestedTags = query(async (videoId: string): Promise<SuggestedTag[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT id, video_id, tag, reason, confidence, status, created_at, actioned_at
    FROM suggested_tags
    WHERE video_id = ?
    ORDER BY 
      CASE status WHEN 'pending' THEN 0 ELSE 1 END,
      confidence DESC
  `).bind(videoId).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    videoId: row.video_id,
    tag: row.tag,
    reason: row.reason,
    confidence: row.confidence ?? 0.8,
    status: row.status ?? "pending",
    createdAt: row.created_at,
    actionedAt: row.actioned_at,
  }));
}, "suggested-tags");

export const getEntities = query(async (filters?: {
  type?: string;
  limit?: number;
}): Promise<Entity[]> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const limit = filters?.limit ?? 100;
  
  let sql = `SELECT id, type, name, canonical_name, aliases, external_ids, metadata, created_at FROM entities`;
  const binds: unknown[] = [];
  
  if (filters?.type) {
    sql += ` WHERE type = ?`;
    binds.push(filters.type);
  }
  
  sql += ` ORDER BY name LIMIT ?`;
  binds.push(limit);
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    type: row.type,
    name: row.name,
    canonicalName: row.canonical_name,
    aliases: row.aliases ? JSON.parse(row.aliases) : [],
    externalIds: row.external_ids ? JSON.parse(row.external_ids) : undefined,
    metadata: row.metadata ? JSON.parse(row.metadata) : undefined,
    createdAt: row.created_at,
  }));
}, CACHE_KEYS.ENTITIES);

export const getEntity = query(async (id: string): Promise<Entity> => {
  "use server";
  
  await requireUserId();
  const DB = requireDb();
  const { results } = await DB.prepare(`
    SELECT id, type, name, canonical_name, aliases, external_ids, metadata, created_at
    FROM entities WHERE id = ?
  `).bind(id).all();
  
  if (results.length === 0) throw new Error("Entity not found");
  
  const row = results[0] as any;
  return {
    id: row.id,
    type: row.type,
    name: row.name,
    canonicalName: row.canonical_name,
    aliases: row.aliases ? JSON.parse(row.aliases) : [],
    externalIds: row.external_ids ? JSON.parse(row.external_ids) : undefined,
    metadata: row.metadata ? JSON.parse(row.metadata) : undefined,
    createdAt: row.created_at,
  };
}, "entity");

// ============================================================================
// PWA / PUSH NOTIFICATION QUERIES (v4.3)
// ============================================================================

export interface PushSubscription {
  id: string;
  userId: string;
  endpoint: string;
  userAgent?: string;
  createdAt: string;
  lastUsedAt?: string;
}

export const getPushSubscriptions = query(async (): Promise<PushSubscription[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  
  const { results } = await DB.prepare(`
    SELECT id, user_id, endpoint, user_agent, created_at, last_used_at
    FROM push_subscriptions
    WHERE user_id = ?
    ORDER BY created_at DESC
  `).bind(userId).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    userId: row.user_id,
    endpoint: row.endpoint,
    userAgent: row.user_agent,
    createdAt: row.created_at,
    lastUsedAt: row.last_used_at,
  }));
}, CACHE_KEYS.PUSH_SUBSCRIPTIONS);

export interface OfflineAction {
  id: string;
  userId: string;
  actionType: string;
  payload: Record<string, unknown>;
  createdAt: string;
  syncedAt?: string;
  syncStatus: "pending" | "synced" | "conflict" | "failed";
  conflictResolution?: Record<string, unknown>;
  errorMessage?: string;
}

export const getOfflineActions = query(async (status?: string): Promise<OfflineAction[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  
  let sql = `
    SELECT id, user_id, action_type, payload, created_at, synced_at, 
           sync_status, conflict_resolution, error_message
    FROM offline_actions
    WHERE user_id = ?
  `;
  const binds: unknown[] = [userId];
  
  if (status) {
    sql += ` AND sync_status = ?`;
    binds.push(status);
  }
  
  sql += ` ORDER BY created_at DESC LIMIT 100`;
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    userId: row.user_id,
    actionType: row.action_type,
    payload: row.payload ? JSON.parse(row.payload) : {},
    createdAt: row.created_at,
    syncedAt: row.synced_at,
    syncStatus: row.sync_status ?? "pending",
    conflictResolution: row.conflict_resolution ? JSON.parse(row.conflict_resolution) : undefined,
    errorMessage: row.error_message,
  }));
}, CACHE_KEYS.OFFLINE_ACTIONS);

export interface PushNotificationLogEntry {
  id: string;
  userId: string;
  notificationType: "flash_video" | "prediction_due" | "briefing_ready" | "system";
  title: string;
  body?: string;
  data?: Record<string, unknown>;
  status: "pending" | "sent" | "failed" | "clicked";
  sentAt?: string;
  clickedAt?: string;
  errorMessage?: string;
  createdAt: string;
}

export const getPushNotificationLog = query(async (filters?: {
  type?: string;
  status?: string;
  limit?: number;
}): Promise<PushNotificationLogEntry[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const limit = filters?.limit ?? 50;
  
  let sql = `
    SELECT id, user_id, notification_type, title, body, data, status,
           sent_at, clicked_at, error_message, created_at
    FROM push_notifications_log
    WHERE user_id = ?
  `;
  const binds: unknown[] = [userId];
  
  if (filters?.type) {
    sql += ` AND notification_type = ?`;
    binds.push(filters.type);
  }
  if (filters?.status) {
    sql += ` AND status = ?`;
    binds.push(filters.status);
  }
  
  sql += ` ORDER BY created_at DESC LIMIT ?`;
  binds.push(limit);
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    userId: row.user_id,
    notificationType: row.notification_type,
    title: row.title,
    body: row.body,
    data: row.data ? JSON.parse(row.data) : undefined,
    status: row.status ?? "pending",
    sentAt: row.sent_at,
    clickedAt: row.clicked_at,
    errorMessage: row.error_message,
    createdAt: row.created_at,
  }));
}, CACHE_KEYS.PUSH_NOTIFICATION_LOG);

// ============================================================================
// SCHEDULED ANALYSES QUERIES (v4.4)
// ============================================================================

export interface ScheduledAnalysis {
  id: string;
  userId: string;
  name: string;
  description?: string;
  templateId?: string;
  templateName?: string;
  scopeType: "channel" | "topic" | "all";
  scopeIds: string[];
  scopeNames?: string[]; // Resolved channel/topic names
  frequency: "daily" | "weekly" | "on_new_video";
  minVideos: number;
  lastRunAt?: string;
  nextRunAt?: string;
  runCount: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduledAnalysisFilters {
  enabled?: boolean;
  frequency?: string;
  templateId?: string;
}

export const getScheduledAnalyses = query(async (filters?: ScheduledAnalysisFilters): Promise<ScheduledAnalysis[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  
  let sql = `
    SELECT sa.*, at.name as template_name
    FROM scheduled_analyses sa
    LEFT JOIN analysis_templates at ON at.id = sa.template_id
    WHERE sa.user_id = ?
  `;
  const binds: unknown[] = [userId];
  
  if (filters?.enabled !== undefined) {
    sql += ` AND sa.enabled = ?`;
    binds.push(filters.enabled ? 1 : 0);
  }
  if (filters?.frequency) {
    sql += ` AND sa.frequency = ?`;
    binds.push(filters.frequency);
  }
  if (filters?.templateId) {
    sql += ` AND sa.template_id = ?`;
    binds.push(filters.templateId);
  }
  
  sql += ` ORDER BY sa.enabled DESC, sa.next_run_at ASC NULLS LAST, sa.name`;
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  
  // Resolve scope names for display
  const schedules: ScheduledAnalysis[] = [];
  for (const row of results as any[]) {
    const scopeIds: string[] = row.scope_ids ? JSON.parse(row.scope_ids) : [];
    let scopeNames: string[] = [];
    
    if (scopeIds.length > 0 && row.scope_type !== "all") {
      const tableName = row.scope_type === "channel" ? "channels" : "topics";
      const placeholders = scopeIds.map(() => "?").join(",");
      const { results: nameResults } = await DB.prepare(
        `SELECT name FROM ${tableName} WHERE id IN (${placeholders})`
      ).bind(...scopeIds).all();
      scopeNames = (nameResults as any[]).map(r => r.name);
    }
    
    schedules.push({
      id: row.id,
      userId: row.user_id,
      name: row.name,
      description: row.description,
      templateId: row.template_id,
      templateName: row.template_name,
      scopeType: row.scope_type ?? "all",
      scopeIds,
      scopeNames,
      frequency: row.frequency ?? "on_new_video",
      minVideos: row.min_videos ?? 1,
      lastRunAt: row.last_run_at,
      nextRunAt: row.next_run_at,
      runCount: row.run_count ?? 0,
      enabled: row.enabled === 1,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    });
  }
  
  return schedules;
}, CACHE_KEYS.SCHEDULED_ANALYSES);

export const getScheduledAnalysis = query(async (id: string): Promise<ScheduledAnalysis> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  
  const { results } = await DB.prepare(`
    SELECT sa.*, at.name as template_name
    FROM scheduled_analyses sa
    LEFT JOIN analysis_templates at ON at.id = sa.template_id
    WHERE sa.id = ? AND sa.user_id = ?
  `).bind(id, userId).all();
  
  if (results.length === 0) throw new Error("Scheduled analysis not found");
  
  const row = results[0] as any;
  const scopeIds: string[] = row.scope_ids ? JSON.parse(row.scope_ids) : [];
  let scopeNames: string[] = [];
  
  if (scopeIds.length > 0 && row.scope_type !== "all") {
    const tableName = row.scope_type === "channel" ? "channels" : "topics";
    const placeholders = scopeIds.map(() => "?").join(",");
    const { results: nameResults } = await DB.prepare(
      `SELECT name FROM ${tableName} WHERE id IN (${placeholders})`
    ).bind(...scopeIds).all();
    scopeNames = (nameResults as any[]).map(r => r.name);
  }
  
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    description: row.description,
    templateId: row.template_id,
    templateName: row.template_name,
    scopeType: row.scope_type ?? "all",
    scopeIds,
    scopeNames,
    frequency: row.frequency ?? "on_new_video",
    minVideos: row.min_videos ?? 1,
    lastRunAt: row.last_run_at,
    nextRunAt: row.next_run_at,
    runCount: row.run_count ?? 0,
    enabled: row.enabled === 1,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}, "scheduled-analysis");

export interface ScheduledAnalysisRun {
  id: string;
  scheduledAnalysisId: string;
  analysisRunId?: string;
  status: "pending" | "running" | "completed" | "failed";
  videosProcessed: number;
  videoIds: string[];
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
  createdAt: string;
}

export const getScheduledAnalysisRuns = query(async (scheduleId: string, options?: {
  limit?: number;
  status?: string;
}): Promise<ScheduledAnalysisRun[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  const limit = options?.limit ?? 20;
  
  let sql = `
    SELECT id, scheduled_analysis_id, analysis_run_id, status,
           videos_processed, video_ids, started_at, completed_at, error_message, created_at
    FROM scheduled_analysis_runs sar
    JOIN scheduled_analyses sa ON sa.id = sar.scheduled_analysis_id
    WHERE sar.scheduled_analysis_id = ?
      AND sa.user_id = ?
  `;
  const binds: unknown[] = [scheduleId, userId];
  
  if (options?.status) {
    sql += ` AND status = ?`;
    binds.push(options.status);
  }
  
  sql += ` ORDER BY created_at DESC LIMIT ?`;
  binds.push(limit);
  
  const { results } = await DB.prepare(sql).bind(...binds).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    scheduledAnalysisId: row.scheduled_analysis_id,
    analysisRunId: row.analysis_run_id,
    status: row.status ?? "pending",
    videosProcessed: row.videos_processed ?? 0,
    videoIds: row.video_ids ? JSON.parse(row.video_ids) : [],
    startedAt: row.started_at,
    completedAt: row.completed_at,
    errorMessage: row.error_message,
    createdAt: row.created_at,
  }));
}, "scheduled-analysis-runs");

// ============================================================================
// REPORT COMPARISON QUERIES (v4.4)
// ============================================================================

export interface ReportComparisonDiff {
  field: string;
  label: string;
  report1Value: string | null;
  report2Value: string | null;
  changeType: "added" | "removed" | "modified" | "unchanged";
}

export interface ReportComparison {
  report1: Report;
  report2: Report;
  diffs: ReportComparisonDiff[];
  addedSections: string[];
  removedSections: string[];
  modifiedSections: string[];
  summary: string;
  generatedAt: string;
}

export const getReportComparison = query(async (reportId1: string, reportId2: string): Promise<ReportComparison> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  
  // Get both reports
  const { results: results1 } = await DB.prepare(`
    SELECT * FROM reports WHERE id = ? AND user_id = ?
  `).bind(reportId1, userId).all();
  
  const { results: results2 } = await DB.prepare(`
    SELECT * FROM reports WHERE id = ? AND user_id = ?
  `).bind(reportId2, userId).all();
  
  if (results1.length === 0) throw new Error("Report 1 not found");
  if (results2.length === 0) throw new Error("Report 2 not found");
  
  const report1 = mapReportRow(results1[0] as any);
  const report2 = mapReportRow(results2[0] as any);
  
  // Parse content for comparison
  const content1 = typeof report1.content === "string" ? JSON.parse(report1.content || "{}") : report1.content;
  const content2 = typeof report2.content === "string" ? JSON.parse(report2.content || "{}") : report2.content;
  
  const diffs: ReportComparisonDiff[] = [];
  const addedSections: string[] = [];
  const removedSections: string[] = [];
  const modifiedSections: string[] = [];
  
  // Compare top-level metadata
  const metadataFields = ["name", "type"];
  for (const field of metadataFields) {
    const val1 = (report1 as any)[field];
    const val2 = (report2 as any)[field];
    
    if (val1 !== val2) {
      diffs.push({
        field,
        label: field.charAt(0).toUpperCase() + field.slice(1),
        report1Value: val1 ?? null,
        report2Value: val2 ?? null,
        changeType: val1 === undefined ? "added" : val2 === undefined ? "removed" : "modified",
      });
    }
  }
  
  // Compare content sections
  const sections1 = new Set(Object.keys(content1));
  const sections2 = new Set(Object.keys(content2));
  
  // Find added sections (in report2 but not in report1)
  for (const section of sections2) {
    if (!sections1.has(section)) {
      addedSections.push(section);
      diffs.push({
        field: `content.${section}`,
        label: section,
        report1Value: null,
        report2Value: JSON.stringify(content2[section]).slice(0, 200),
        changeType: "added",
      });
    }
  }
  
  // Find removed sections (in report1 but not in report2)
  for (const section of sections1) {
    if (!sections2.has(section)) {
      removedSections.push(section);
      diffs.push({
        field: `content.${section}`,
        label: section,
        report1Value: JSON.stringify(content1[section]).slice(0, 200),
        report2Value: null,
        changeType: "removed",
      });
    }
  }
  
  // Find modified sections (in both but different)
  for (const section of sections1) {
    if (sections2.has(section)) {
      const str1 = JSON.stringify(content1[section]);
      const str2 = JSON.stringify(content2[section]);
      
      if (str1 !== str2) {
        modifiedSections.push(section);
        diffs.push({
          field: `content.${section}`,
          label: section,
          report1Value: str1.slice(0, 200),
          report2Value: str2.slice(0, 200),
          changeType: "modified",
        });
      }
    }
  }
  
  // Generate summary
  const changes: string[] = [];
  if (addedSections.length > 0) changes.push(`${addedSections.length} section(s) added`);
  if (removedSections.length > 0) changes.push(`${removedSections.length} section(s) removed`);
  if (modifiedSections.length > 0) changes.push(`${modifiedSections.length} section(s) modified`);
  const summary = changes.length > 0 ? changes.join(", ") : "No changes detected";
  
  return {
    report1,
    report2,
    diffs,
    addedSections,
    removedSections,
    modifiedSections,
    summary,
    generatedAt: new Date().toISOString(),
  };
}, "report-comparison");

export interface ComparableReport {
  id: string;
  name: string;
  type: string;
  createdAt: string;
}

export const getComparableReports = query(async (reportId: string): Promise<ComparableReport[]> => {
  "use server";
  
  const DB = requireDb();
  const userId = await requireUserId();
  
  // Get the source report to find comparable ones
  const { results: sourceResults } = await DB.prepare(`
    SELECT type FROM reports WHERE id = ? AND user_id = ?
  `).bind(reportId, userId).all();
  
  if (sourceResults.length === 0) throw new Error("Report not found");
  
  const source = sourceResults[0] as any;
  
  // Find reports of same type (and optionally same scope)
  const { results } = await DB.prepare(`
    SELECT id, name, type, created_at
    FROM reports
    WHERE id != ?
      AND type = ?
      AND user_id = ?
      AND status = 'completed'
    ORDER BY created_at DESC
    LIMIT 20
  `).bind(reportId, source.type, userId).all();
  
  return (results as any[]).map(row => ({
    id: row.id,
    name: row.name,
    type: row.type,
    createdAt: row.created_at,
  }));
}, "comparable-reports");
